import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { checkAuthStatusSuccess } from '../shared/store/actions';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly platformId = inject(PLATFORM_ID);

  private authStatus$?: Observable<boolean>;
  private lastAttemptedUrl: string | null = null;

  storeRedirectUrl(url: string | null): void {
    this.lastAttemptedUrl = url;
  }

  consumeRedirectUrl(): string | null {
    const redirectUrl = this.lastAttemptedUrl;
    this.lastAttemptedUrl = null;
    return redirectUrl;
  }

  ensureAuthenticated(): Observable<boolean> {
    if (!this.authStatus$) {
      this.authStatus$ = this.authService.isAuthenticated().pipe(
        tap((auth) => {
          if (auth.authenticated) {
            this.store.dispatch(checkAuthStatusSuccess(auth));
          }
        }),
        map((auth) => {
          if (auth.authenticated) {
            return true;
          }

          this.clear();
          this.storeRedirectUrl(this.router.url);
          this.router.navigate(['/login'], {
            queryParams: { redirectTo: this.lastAttemptedUrl },
          });
          return false;
        }),
        catchError(() => {
          this.clear();
          this.storeRedirectUrl(this.router.url);
          this.router.navigate(['/login'], {
            queryParams: { redirectTo: this.lastAttemptedUrl },
          });
          return of(false);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.authStatus$;
  }

  clear(): void {
    this.authStatus$ = undefined;
  }
}
